// src/oura/components/client/GbpScreenshots.tsx
//
// The before-screenshots for one outlet (#4010): what the profile looked like
// when we started, one shot per section, so a later claim about what changed
// has something to stand on.
//
// Its own file rather than a section inside GbpProfileCard: it owns its
// uploads, its own refusals and its own two service calls, and nothing in the
// field form needs any of it.

import { useRef, useState } from "react";
import { ImageIcon, Loader2Icon, TrashIcon, UploadIcon } from "lucide-react";
import {
    addGbpScreenshot,
    removeGbpScreenshot,
    GBP_CAPTION_MAX,
    GBP_MAX_SHOTS,
    GBP_SHOT_MAX_BYTES,
    GBP_SHOT_TYPES,
    type GbpOutlet,
    type GbpProfiles,
    type GbpResult,
    type GbpShot,
} from "@service/aeo/gbpProfile";
import { useSnackbar } from "@hook/use-snackbar";
import { Field, Refusal } from "../onboarding/fields";
import { inputClass } from "../../onboarding/messageText";
import { shotLabelText } from "../../gbp/model";
import { cn } from "../../lib/cn";
import { dayDate } from "../../lib/oura";
import { FOCUS_RING, PillButton, SURFACE, TYPE } from "@oura/ui";

/** The before-screenshots for one outlet: add one per section, remove a wrong one. */
export function GbpScreenshots({
    businessId,
    outlet,
    shotLabels,
    onPayload,
    claim,
    release,
    locked,
}: {
    businessId: string;
    outlet: GbpOutlet;
    shotLabels: string[];
    onPayload: (data: GbpProfiles, from: string) => void;
    /**
     * The OUTLET's mutation lock, not this component's. The field save answers
     * with a whole profiles payload too, so serialising only the screenshots
     * left a save racing an upload (Jack, #4056). `claim` is synchronous and
     * returns false when something else holds it.
     */
    claim: () => boolean;
    release: () => void;
    /** Something on this outlet is mid-flight, including the field save. */
    locked: boolean;
}) {
    const snackbar = useSnackbar();
    const [label, setLabel] = useState(shotLabels[0] ?? "other");
    const [caption, setCaption] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [busy, setBusy] = useState(false);
    const [removing, setRemoving] = useState<string | null>(null);
    const [refused, setRefused] = useState<string | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    const full = outlet.screenshots.length >= GBP_MAX_SHOTS;

    const pick = (f: File | undefined) => {
        setRefused(null);
        if (!f) { setFile(null); return; }
        // The server's own two rules, said before the file goes over the wire.
        if (!GBP_SHOT_TYPES.includes(f.type)) { setRefused("A screenshot is a PNG, JPG or WebP image."); setFile(null); return; }
        if (f.size > GBP_SHOT_MAX_BYTES) { setRefused("A screenshot is 20 MB or less."); setFile(null); return; }
        setFile(f);
    };

    const after = (r: GbpResult, done: string) => {
        if (!r.ok) { setRefused(r.message); return false; }
        snackbar.showSuccess(done);
        onPayload(r.data, businessId);
        return true;
    };

    const add = async () => {
        if (!file || !claim()) return;
        setBusy(true);
        setRefused(null);
        // `finally`, because a lock the card shares must come back even if the
        // call rejects. The service is written to resolve either way, but a
        // broken promise anywhere under it would otherwise leave every control
        // on the card dead until a reload (Jack, #4056).
        try {
            const r = await addGbpScreenshot(businessId, outlet.outlet, { file, label, caption });
            if (after(r, "Screenshot added")) {
                setFile(null);
                setCaption("");
                if (fileRef.current) fileRef.current.value = "";
            }
        } finally {
            release();
            setBusy(false);
        }
    };

    const remove = async (shot: GbpShot) => {
        if (!claim()) return;
        setRemoving(shot.id);
        setRefused(null);
        try {
            after(await removeGbpScreenshot(businessId, outlet.outlet, shot.id), "Screenshot removed");
        } finally {
            release();
            setRemoving(null);
        }
    };

    return (
        <div className={cn(SURFACE.block, "mt-3 space-y-3")}>
            <h4 className={cn(TYPE.tb, "m-0 font-semibold text-gray-900")}>
                Before-screenshots{" "}
                <span className="font-normal text-gray-500">
                    {outlet.screenshots.length} of {GBP_MAX_SHOTS}
                </span>
            </h4>

            {outlet.screenshots.length > 0 && (
                <ul className="list-plain m-0 grid grid-cols-2 gap-3 p-0 sm:grid-cols-3">
                    {outlet.screenshots.map((shot) => (
                        <li key={shot.id} className="min-w-0 rounded-xl bg-white ring-1 ring-inset ring-gray-200">
                            {/* The link, not the image, is the control: the URL
                                is presigned for an hour, so a thumbnail that
                                has expired still has a row that says what it
                                is. */}
                            <a
                                href={shot.url ?? undefined}
                                target="_blank"
                                rel="noreferrer"
                                className={cn("block aspect-video overflow-hidden rounded-t-xl bg-gray-50", FOCUS_RING)}
                            >
                                {shot.url ? (
                                    <img src={shot.url} alt={shot.caption || shotLabelText(shot.label)} className="h-full w-full object-cover" />
                                ) : (
                                    <span className="flex h-full w-full items-center justify-center text-gray-400">
                                        <ImageIcon aria-hidden size={18} />
                                    </span>
                                )}
                            </a>
                            <div className="flex items-start gap-2 p-2">
                                <span className="min-w-0 flex-1">
                                    <span className={cn(TYPE.tc, "block font-medium text-gray-900")}>{shotLabelText(shot.label)}</span>
                                    {shot.caption && <span className={cn(TYPE.tc, "block text-gray-500")}>{shot.caption}</span>}
                                    <span className={cn(TYPE.tc, "block text-gray-400")}>
                                        {[shot.addedBy, dayDate(shot.addedAt)].filter(Boolean).join(" · ")}
                                    </span>
                                </span>
                                <button
                                    type="button"
                                    aria-label={`Remove the ${shotLabelText(shot.label)} screenshot`}
                                    disabled={busy || removing !== null || locked}
                                    onClick={() => void remove(shot)}
                                    className={cn("shrink-0 rounded p-1 text-gray-400 hover:text-rose-600 disabled:opacity-50", FOCUS_RING)}
                                >
                                    {removing === shot.id
                                        ? <Loader2Icon aria-hidden size={14} className="animate-spin" />
                                        : <TrashIcon aria-hidden size={14} />}
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {full ? (
                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>
                    At most {GBP_MAX_SHOTS} screenshots per outlet. Remove one to add another.
                </p>
            ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <Field label="Section">
                        {({ id }) => (
                            <select id={id} className={inputClass()} value={label} disabled={busy || removing !== null || locked} onChange={(e) => setLabel(e.target.value)}>
                                {shotLabels.map((l) => (
                                    <option key={l} value={l}>{shotLabelText(l)}</option>
                                ))}
                            </select>
                        )}
                    </Field>
                    <Field label="Caption" hint="Optional.">
                        {({ id }) => (
                            <input
                                id={id}
                                value={caption}
                                maxLength={GBP_CAPTION_MAX}
                                autoComplete="off"
                                disabled={busy || removing !== null || locked}
                                onChange={(e) => setCaption(e.target.value)}
                                className={inputClass()}
                            />
                        )}
                    </Field>
                    <Field label="Image" hint="PNG, JPG or WebP.">
                        {({ id }) => (
                            <input
                                id={id}
                                ref={fileRef}
                                type="file"
                                accept={GBP_SHOT_TYPES.join(",")}
                                disabled={busy || removing !== null || locked}
                                onChange={(e) => pick(e.target.files?.[0])}
                                className={cn(TYPE.tc, "block w-full text-gray-500")}
                            />
                        )}
                    </Field>
                </div>
            )}

            {refused && <Refusal>{refused}</Refusal>}

            {!full && (
                <PillButton className="max-sm:h-9" busy={busy} disabled={!file || removing !== null || locked} onClick={() => void add()}>
                    <UploadIcon aria-hidden size={14} />
                    Add the screenshot
                </PillButton>
            )}
        </div>
    );
}
